/*
 * ******************************************************************
 * Copyright (c) 2024-2025 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { AfterViewInit, Component, EventEmitter, Input, NgZone, OnDestroy, Output, QueryList, ViewChildren } from "@angular/core";
import { ClrCombobox, ClrDatagridFilter, ClrDatagridFilterInterface, ClrPopoverEventsService, ClrPopoverToggleService } from "@clr/angular";
import { EnumDescriptor } from "@shared/models/server-driven-filter.model";
import { L10nService } from "@vmw/ngx-vip";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

export class EnumItemModel {
    constructor(public label: string,
                public value: string,
                public selected: boolean = false) {
    }
}

// @formatter:off
@Component({
    standalone: false,
    selector: 'dg-searchable-filter',
    templateUrl: './datagrid-searchable-filter.component.html',
    styleUrl: './datagrid-searchable-filter.component.scss'
})
// @formatter:on
export class DatagridSearchableFilter<T> implements ClrDatagridFilterInterface<T>, AfterViewInit, OnDestroy {
    @ViewChildren("combobox")
    comboboxQuery: QueryList<ClrCombobox<any>>;

    @Input()
    public filterProperty: string;

    @Input()
    public sort: boolean = true;

    private combobox: ClrCombobox<any>;

    /**
     * Resource strings for enum values used to show in the filter.
     * @param value - array of strings
     */
    @Input()
    public set enumDescriptors(value: EnumDescriptor[]) {
        if (value && value.length > 0) {
            this._enumDescriptors = value;
            this.initFilter();
        }
    }

    public get enumDescriptors(): EnumDescriptor[] {
        return this._enumDescriptors;
    }

    /**
     * Preset filter selection.
     * @param value - array of strings
     */
    @Input()
    public set clrFilterValue(value: string) {
        this._initialValue = value;
        this.presetFilter();
    }
    public get clrFilterValue(): string {
        return this.selectedItem.value;
    }

    @Output()
    clrFilterValueChange: EventEmitter<string> = new EventEmitter();

    public items: Array<EnumItemModel>;
    public selectedItem: EnumItemModel = null;
    private _enumDescriptors: EnumDescriptor[];
    private _initialValue: string;
    private changesSubject: Subject<string> = new Subject();
    private destroySubject: Subject<void> = new Subject<void>();

    constructor(protected filterContainer: ClrDatagridFilter,
                private clrPopoverEventsService: ClrPopoverEventsService,
                private smartToggleService: ClrPopoverToggleService,
                private ngZone: NgZone,
                protected l10nService: L10nService) {
        filterContainer.setFilter(this);
    }

    ngAfterViewInit(): void {
        this.comboboxQuery.map(comboboxContainer => {
            this.combobox = comboboxContainer;
        });
        this.smartToggleService.openChange
            .pipe(takeUntil(this.destroySubject))
            .subscribe(() => {
                this.ngZone.runOutsideAngular(() => {
                    requestAnimationFrame(() => {
                        this.combobox.textbox.nativeElement.focus();
                    });
                });
            });
    }

    switchEventListeners(open: boolean): void {
        if (open) {
            this.clrPopoverEventsService.removeClickListener();
            this.clrPopoverEventsService.removeEscapeListener();
            this.clrPopoverEventsService.removeScrollListener();
        } else {
            setTimeout(() => {
                this.clrPopoverEventsService.addClickListener();
                this.clrPopoverEventsService.addEscapeListener();
                this.clrPopoverEventsService.addScrollListener();
            });
        }
    }

    onOpenChange(open: boolean): void {
        this.switchEventListeners(open);
    }

    onSelectionChange(event: any): void {
        this.selectedItem = event.model;
        this.changesSubject.next(this.selectedItem?.value);
        this.clrFilterValueChange.emit(this.selectedItem?.value);
    }

    ngOnDestroy(): void {
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    private initFilter(): void {
        this.items = new Array<EnumItemModel>();
        if (this._enumDescriptors) {
            for (const item of this._enumDescriptors) {
                let str: string;
                if (item.labelResourceId) {
                    str = (item.labelResourceId === "EMPTY" || !this.l10nService.isExistKey(item.labelResourceId)) ?
                          "" : this.l10nService.getMessage(item.labelResourceId);
                } else {
                    str = item.labelResourceValue;
                }
                this.items.push(new EnumItemModel(str, item.enumValue));
                if (this.sort) {
                    this.items.sort((a, b) => a.label < b.label ? -1 : 1);
                }
            }
        }
        this.presetFilter();
    }

    /**
     * The method is not used for server-driven datagrids, so no need to implement it.
     */
    public accepts(unused: T): boolean {
        return false;
    }

    public get state(): any {
        return {
            property: this.filterProperty,
            value: [this.selectedItem.value]
        }
    }

    /**
     * The Observable required as part of the Filter interface
     */
    @Output()
    public get changes(): Observable<string> {
        return this.changesSubject.asObservable();
    }

    /**
     * Indicates if the filter is currently active, (at least one input is set)
     */
    public isActive(): boolean {
        return this.selectedItem != null && this.selectedItem.value != null;
    }

    private presetFilter(): void {
        if (this._initialValue && this.items) {
            const initialItem: EnumItemModel = this.items.find(item => item.value === this._initialValue);
            if (initialItem) {
                this.selectedItem = initialItem;
            } else {
                console.log("%o not found in the filter items.", this._initialValue);
            }
        }
    }

}
